import React from 'react';
import TruncableText from './TruncableText';
import './styles.scss';

// Replace literal -> with → and <- with ←. Returns input unchanged if not a string.
const replaceArrows = (input) => {
    if (typeof input !== 'string') return input;
    return input.replace(/->/g, '→').replace(/<-/g, '←');
};

const hasNewline = (input) => typeof input === 'string' && input.includes('\n');

// Tracking presets map to the CSS custom properties defined in typography.scss.
const TRACKING_PRESETS = new Set(['tight', 'snug', 'normal', 'wide', 'wider']);

// Create sub-components for each text variant.
// `variantDefaults` lets a variant ship its own sensible defaults (e.g. an
// eyebrow defaults to a muted contrast) while still honouring caller overrides.
const createTextVariant = (tag, variantClassName, variantDefaults = {}) => {
    return React.forwardRef(({
        contrast = variantDefaults.contrast ?? 10,
        weight = variantDefaults.weight ?? null,
        uppercase = variantDefaults.uppercase ?? false,
        tracking = variantDefaults.tracking ?? null,
        left,
        right,
        maxLines,
        grow,
        loading = false,
        animated = false,
        animatedCharDelay = 0.05,
        className: userClassName,
        children,
        value,
        style,
        ...props
    }, ref) => {
        const processedChildren = replaceArrows(children);
        const processedValue = replaceArrows(value);
        const needsPreLine = hasNewline(processedChildren) || hasNewline(processedValue);
        const Component = tag;
        const contrastClass = `csf-${11 - contrast}`;
        const growClass = grow ? 'text-grow' : '';
        const loadingClass = loading ? 'text-component--loading' : '';
        const upperClass = uppercase ? 'text-uppercase' : '';
        // `tracking` accepts a preset key (tight|snug|normal|wide|wider) or any
        // raw CSS length/keyword, which is applied inline.
        const trackingClass = tracking && TRACKING_PRESETS.has(tracking)
            ? `text-tracking-${tracking}`
            : '';
        let combinedClassName = `text-component ${contrastClass} ${growClass} ${loadingClass} ${upperClass} ${trackingClass}`.replace(/\s+/g, ' ').trim();

        // A raw (non-preset) tracking value is applied inline as letter-spacing.
        const rawTracking = tracking && !TRACKING_PRESETS.has(tracking) ? tracking : null;
        let baseStyle = weight ? { ...style, fontWeight: weight } : style;
        if (rawTracking) baseStyle = { ...(baseStyle || {}), letterSpacing: rawTracking };
        const finalStyle = needsPreLine ? { ...(baseStyle || {}), whiteSpace: 'pre-line' } : baseStyle;

        // Determine gap size based on tag
        const isLargeHeading = ['h1', 'h2', 'h3'].includes(tag);
        const gap = isLargeHeading ? 'var(--gap-2)' : 'var(--gap-1)';

        // Clone left/right components to apply text-icon class
        const cloneWithSize = (element) => {
            if (!element) return null;

            // If it's a valid React element, clone it with text-icon class
            if (React.isValidElement(element)) {
                return React.cloneElement(element, {
                    className: `text-icon ${contrastClass} ${element.props.className || ''}`.trim(),
                    style: {
                        ...element.props.style
                    }
                });
            }

            return element;
        };

        const leftElement = cloneWithSize(left);
        const rightElement = cloneWithSize(right);

        // Create wrapper class with variant for icon sizing
        let wrapperClass = `text-wrapper text-wrapper-${tag} ${growClass}`.trim();

        if (left || right) {
            wrapperClass = `${wrapperClass} ${variantClassName} ${userClassName || ''}`.trim();
        } else {
            combinedClassName = `${combinedClassName} ${variantClassName} ${userClassName || ''}`.trim();
        }

        const resolveAnimatedString = () => {
            if (typeof processedChildren === 'string') return processedChildren;
            if (processedValue != null && processedValue !== '') return String(processedValue);
            return null;
        };

        const renderBody = () => {
            if (animated) {
                const str = resolveAnimatedString();
                if (str != null) {
                    return str.split('').map((char, index) => {
                        if (char === '\n') {
                            return <br key={`br-${index}`} />;
                        }
                        return (
                            <span
                                key={index}
                                className="text-animated-char"
                                style={{ animationDelay: `${index * animatedCharDelay}s` }}
                            >
                                {char}
                            </span>
                        );
                    });
                }
            }
            return processedChildren || processedValue;
        };

        // If maxLines is provided, use TruncableText (not combined with animated)
        if (maxLines && !animated) {
            const content = (
                <>
                    {leftElement}
                    <TruncableText
                        text={(typeof processedChildren === 'string' ? processedChildren : '') || processedValue}
                        maxLines={maxLines}
                        component={Component}
                        className={combinedClassName}
                        style={finalStyle}
                        {...props}
                    />
                    {rightElement}
                </>
            );

            if (leftElement || rightElement) {
                return (
                    <div
                        ref={ref}
                        className={wrapperClass}
                        style={{ gap }}
                    >
                        {content}
                    </div>
                );
            }

            return content;
        }

        // Regular text without truncation (or per-character animation)
        const content = (
            <Component
                ref={ref}
                className={combinedClassName}
                style={finalStyle}
                {...props}
            >
                {renderBody()}
            </Component>
        );

        if (leftElement || rightElement) {
            return (
                <div
                    ref={ref}
                    className={wrapperClass}
                    style={{ gap }}
                >
                    {leftElement}
                    {content}
                    {rightElement}
                </div>
            );
        }

        return content;
    });
};

// Create the main Text object with sub-components
const Text = {
    h1: createTextVariant('h1', 'text-h1'),
    h2: createTextVariant('h2', 'text-h2'),
    h3: createTextVariant('h3', 'text-h3'),
    h4: createTextVariant('h4', 'text-h4'),
    h5: createTextVariant('h5', 'text-h5'),
    h6: createTextVariant('h6', 'text-h6'),
    p: createTextVariant('p', 'text-p'),
    span: createTextVariant('span', 'text-span'),
    small: createTextVariant('small', 'text-small'),

    // ── Role-based variants (AlignUI vocabulary) ──────────────────────────
    // Eyebrow / section header — UPPERCASE, tracked out, muted by default.
    // Use over a stronger title for KPI labels, table headers, settings
    // sections, dropdown group labels. (styling baked into .text-subheading)
    subheading: createTextVariant('span', 'text-subheading', { contrast: 6 }),
    // UI-control label — medium weight, strong contrast, sentence case. The
    // weighty twin of a paragraph; use for form field labels and menu items.
    label: createTextVariant('span', 'text-label', { contrast: 9 }),
    // Caption / meta / fine print — small, regular weight, muted.
    caption: createTextVariant('span', 'text-caption', { contrast: 6 }),
};

export default Text;